import { SortingState, Table } from '@tanstack/react-table';

import { useEffect, useRef, useState } from 'react';

import { fitBounds } from '../../../modules/referenceView/Tkv2MlsMap/fitBounds.ts';
import { Feature } from '../../../modules/referenceView/Tkv2MlsMap/types.ts';
import { MAP_SOURCES, MAP_STYLES } from '../../Tkv2Map/consts.ts';
import MapBox from '../../map/MapBox.ts';
import {
  DEFAULT_SELECTED_STATUSES,
  MAP_LAYERS,
  MAP_LAYER_DEFINITIONS,
  MAP_PIN_STATUS_FILTERS,
} from '../../map/constants.ts';
import { Pin, PinStatusFilter } from '../../map/types.ts';
import { useMap } from '../../map/useMap.ts';
import { propertyPinsToGeoJSON } from '../../map/usePinLayerCallbacks.ts';
import { CompSelectionProperty, CompSelectionState } from '../types.ts';

const DEFAULT_ZOOM = 14;

const getMapPinStatus = (
  mlsStatus: string | undefined,
  variant: 'sales' | 'rental',
): PinStatusFilter => {
  const status = mlsStatus?.toLowerCase() ?? '';

  if (status.includes('pending')) {
    return MAP_PIN_STATUS_FILTERS.PENDING;
  }
  if (status === 'active' || status.includes('active')) {
    return MAP_PIN_STATUS_FILTERS.ACTIVE;
  }
  if (
    status.includes('sold') ||
    (variant === 'rental' &&
      (status.includes('rent') || status.includes('leased') || status === 'closed'))
  ) {
    return MAP_PIN_STATUS_FILTERS.SOLD;
  }
  return MAP_PIN_STATUS_FILTERS.OTHER;
};

const getMapPinIcon = (status: PinStatusFilter, variant: 'sales' | 'rental'): Pin['icon'] => {
  if (variant === 'rental' && status === MAP_PIN_STATUS_FILTERS.SOLD) {
    return 'rented';
  }

  return status;
};

export const useCompMap = ({
  selectedPropertyId,
  lat,
  lng,
  subjectLat,
  subjectLng,
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  mapType,
  rowSelection,
  isInspectifyOrder,
  hasArvOption,
  selectedStatuses: controlledSelectedStatuses,
  onSelectedStatusesChange,
  compSelectionTableVariant = 'sales',
}: {
  selectedPropertyId: string;
  lat: number;
  lng: number;
  subjectLat: number;
  subjectLng: number;
  data: CompSelectionProperty[];
  comps: CompSelectionProperty[];
  sorting: SortingState;
  compSelectionTableRef?: React.MutableRefObject<Table<CompSelectionProperty> | undefined>;
  setFocusedRow: (rowId: string | undefined) => void;
  mapType: string;
  rowSelection?: CompSelectionState;
  isInspectifyOrder?: boolean;
  hasArvOption?: boolean;
  selectedStatuses?: PinStatusFilter[];
  onSelectedStatusesChange?: (statuses: PinStatusFilter[]) => void;
  compSelectionTableVariant?: 'sales' | 'rental';
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);
  const propertyLoaded = useRef<boolean>(false);

  const [pins, setPins] = useState<Pin[]>([]);
  const [internalSelectedStatuses, setInternalSelectedStatuses] = useState<PinStatusFilter[]>(
    () => [...DEFAULT_SELECTED_STATUSES],
  );

  const selectedStatuses = controlledSelectedStatuses ?? internalSelectedStatuses;
  const setSelectedStatuses = onSelectedStatusesChange ?? setInternalSelectedStatuses;

  useEffect(() => {
    const rows = compSelectionTableRef?.current?.getSortedRowModel().rows;
    const rowMap = rows
      ? new Map(rows.map((r, i) => [r.original.id, i]))
      : new Map(data.map((property, i) => [property.id, i + 1]));

    const newPins = data.reduce((acc, property) => {
      if (!property.latitude || !property.longitude) return acc;

      const index = rowMap.get(property.id);
      const status = getMapPinStatus(property.mlsStatus, compSelectionTableVariant);

      const selection = rowSelection?.[property.id];
      const isComp =
        selection !== undefined &&
        (!isInspectifyOrder || !hasArvOption ? true : !!selection.aiv || !!selection.arv);

      let valuationType: 'aiv' | 'arv' | undefined;
      if (isInspectifyOrder && hasArvOption && rowSelection && isComp) {
        if (selection?.aiv) {
          valuationType = 'aiv';
        } else if (selection?.arv) {
          valuationType = 'arv';
        }
      }

      acc.push({
        id: property.id,
        lat: property.latitude,
        lng: property.longitude,
        label: index !== undefined ? `${index}` : '',
        active: isComp,
        selected: property.id === selectedPropertyId,
        status: isComp ? MAP_PIN_STATUS_FILTERS['SELECTED COMPS'] : status,
        icon: getMapPinIcon(status, compSelectionTableVariant),
        valuationType,
      });
      return acc;
    }, [] as Pin[]);

    setPins(newPins);
  }, [
    compSelectionTableRef,
    sorting,
    data,
    comps,
    rowSelection,
    isInspectifyOrder,
    hasArvOption,
    selectedPropertyId,
    compSelectionTableVariant,
  ]);

  useMap({
    mapContainerRef,
    mapRef,
    center: { lat: subjectLat, lng: subjectLng },
    zoom: DEFAULT_ZOOM,
    subject: { lat: subjectLat, lng: subjectLng },
    pins,
    selectedStatuses,
    enableStatusFiltering: true,
    statusFilterVariant: compSelectionTableVariant === 'rental' ? 'rental' : 'sales',
    callbacks: {
      onStyleLoad: () => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        if (map?.getSource(MAP_SOURCES.PINS)) {
          map?.removeLayer(MAP_LAYERS.PINS);
          map?.removeSource(MAP_SOURCES.PINS);
        }
        map.addSource(MAP_SOURCES.PINS, propertyPinsToGeoJSON(pins));
        map.addLayer(MAP_LAYER_DEFINITIONS.PINS);
      },
      onPinFilterChange: (statuses: PinStatusFilter[]) => {
        setSelectedStatuses(statuses);
      },
      onPinSourceLoad: (sourceId, target) => {
        if (propertyLoaded.current || (lat === subjectLat && lng === subjectLng)) return;

        fitBounds(target, [
          { latitude: lat, longitude: lng },
          { latitude: subjectLat, longitude: subjectLng },
        ]);

        propertyLoaded.current = true;
      },
      onPinClick: (feature: Feature | undefined) => {
        if (!compSelectionTableRef?.current || !feature?.properties?.id) return;
        const pinId = feature.properties.id;

        const row = compSelectionTableRef.current.getSortedRowModel().rowsById[pinId];
        if (!row) return;

        setFocusedRow(row.id);
      },
    },
  });

  useEffect(() => {
    const url = mapType === 'satellite' ? MAP_STYLES.SATELLITE.url : MAP_STYLES.STREET.url;
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;
    map?.setStyle(url);
  }, [mapType]);

  return { mapContainerRef };
};
